import { cn } from '@askable/ui/lib/utils';
import Mousetrap from 'mousetrap';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'src/lib/routing';
import { useBillingIsTracks } from 'src/hooks/useBillingIsTracks';
import { useProductAccess } from 'src/hooks/useProductAccess';

import { useConnectedClient } from 'context/ConnectedClientContext';
import { utils } from 'lib/utils';

import { ChatHistory } from './components/ChatHistory';
import { Credits } from './components/Credits/Credits';
import { HelpAndSupport } from './components/HelpAndSupport/HelpAndSupport';
import { Navigation } from './components/Navigation';
import { SettingsNavigation } from './components/SettingsNavigation';
import { SidebarToggle } from './components/SidebarToggle';
import { TeamSelector } from './components/TeamSelector';
import { TracksProgress } from './components/TracksProgress';
import { useSidebarCollapsed } from './hooks/useSidebarCollapsed';

// 60px, not 52: the rows keep the same 12px inset when the sidebar collapses, so
// their icons stay at x=22 and never jump as it opens and closes. The rail has to
// be twice that plus the 16px icon for 22 to read as centred — at 52 the icons sat
// 8px left of centre.
const WIDTH_COLLAPSED = 'w-[3.75rem]';
const WIDTH_EXPANDED = 'w-64';
const TRANSITION_DURATION = 150;

export const MainNavSidebar = () => {
  const { t } = useTranslation();
  const { details } = useConnectedClient();
  const { pathname } = useLocation();
  const { enabled: isTracksEnabled } = useBillingIsTracks();
  const { activeProduct } = useProductAccess();
  const [isCollapsed, toggleCollapsed] = useSidebarCollapsed();
  const [isTransitionFinished, setIsTransitionFinished] = useState(true);
  const transitionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSettingsPage = pathname.startsWith('/settings');

  // @todo: replace with actual Tracks data when it's implemented
  const tracks = { active: 0, total: 0 };

  // Handle transition timing for overflow visibility
  useEffect(() => {
    // Clear any existing timer
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
    }

    setIsTransitionFinished(false);

    transitionTimerRef.current = setTimeout(() => {
      setIsTransitionFinished(true);
      transitionTimerRef.current = null;
    }, TRANSITION_DURATION);

    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, [isCollapsed]);

  // Toggle sidebar with cross-platform keyboard shortcut
  useEffect(() => {
    const modifier = utils.getCommandKey();
    const shortcut = modifier === '⌘' ? 'command+/' : 'ctrl+/';
    Mousetrap.bind(shortcut, toggleCollapsed);

    return () => {
      Mousetrap.unbind(shortcut);
    };
  }, [toggleCollapsed]);

  return (
    <>
      <a
        href="#main-content"
        className={cn(
          `absolute left-1/2 z-50 m-3 -translate-x-1/2 -translate-y-16 rounded-md bg-secondary p-3 leading-none shadow-md transition focus:translate-y-0 md:shadow-none`,
          {
            'shadow-md md:shadow-none': isCollapsed,
          },
        )}
      >
        {t('global.skipToContent')}
      </a>
      <div
        aria-expanded={!isCollapsed}
        className={cn(
          // pt-[19px] drops the team switcher onto the same centre line as the page
          // title next to it: the row is 28px, so its centre lands 19 + 14 = 33,
          // which is where the header's own padding puts that title.
          `group/sidebar fixed bottom-0 top-0 z-20 flex shrink-0 flex-col gap-2 border-border bg-sidebar pb-2 pt-[19px] text-sm text-foreground transition-all duration-150 md:relative md:bg-sidebar`,
          {
            [`${WIDTH_EXPANDED} overflow-hidden border-r-0 md:border-r-0`]: !isCollapsed,
            // The rail is the ground layer, so nothing tints it on hover or press:
            // lighting the whole surface reads as the ground changing rather than as
            // an affordance, and the press already expands it, which is feedback
            // enough. Only keyboard focus paints, since the rail is tabbable and has
            // nothing else to show for it.
            [`${WIDTH_COLLAPSED} -translate-x-full cursor-pointer border-r-0 md:translate-x-0 focus-visible:[&:not(:has(*:focus-visible))]:bg-foreground/[0.04] ${isTransitionFinished ? 'overflow-visible' : 'overflow-hidden'}`]:
              isCollapsed,
          },
        )}
        tabIndex={isCollapsed ? 0 : undefined}
        onClick={e => {
          if (!isCollapsed) {
            return;
          }

          // Don't expand when clicking on interactive elements
          const target = e.target as HTMLElement;
          const isInteractiveElement = target.closest('a, button, [role="button"]');

          if (!isInteractiveElement) {
            toggleCollapsed();
          }
        }}
      >
        {isSettingsPage && !isCollapsed ? (
          <SettingsNavigation />
        ) : (
          <>
            <header className="flex items-center px-3">
              {isCollapsed ? (
                <SidebarToggle isCollapsed onToggle={toggleCollapsed} />
              ) : (
                <>
                  <TeamSelector details={details} />
                  <SidebarToggle isCollapsed={false} onToggle={toggleCollapsed} />
                </>
              )}
            </header>

            <Navigation isCollapsed={isCollapsed} teamId={details?.team?.id || ''} />

            <ChatHistory isCollapsed={isCollapsed} />

            <footer className="mt-auto flex flex-col gap-2 px-3">
              {!isCollapsed && isTracksEnabled ? <TracksProgress total={tracks.total} active={tracks.active} /> : null}
              {!isTracksEnabled && activeProduct !== 'industry_stream' ? <Credits isCollapsed={isCollapsed} /> : null}
              <HelpAndSupport isCollapsed={isCollapsed} />
            </footer>
          </>
        )}

        {!isCollapsed ? (
          <button
            className="absolute bottom-0 right-0 top-0 z-20 ml-2 h-screen w-2 cursor-[w-resize]"
            onClick={toggleCollapsed}
          />
        ) : null}
      </div>

      {!isCollapsed ? (
        <div
          className="fixed inset-0 z-[19] bg-black/20 opacity-100 transition-opacity duration-150 starting:opacity-0 md:hidden"
          onClick={toggleCollapsed}
          aria-hidden="true"
        />
      ) : null}
    </>
  );
};
